'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import {
  CONSENT_ALL,
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_SECONDS,
  parseConsent,
  serializeConsent,
  type ConsentCategories,
  type ConsentDecision,
} from '@/lib/analytics/consent'
import { setEffectiveConsent } from '@/lib/analytics/consent-state'

type ConsentContextValue = {
  /**
   * The active decision, or null when the visitor has not chosen yet — always
   * read from the browser's own cookie, never from the server-rendered value
   * (see `resolved`).
   */
  decision: ConsentDecision | null
  /**
   * Whether `decision` has been read from `document.cookie` yet. False during
   * SSR and during the hydration render that has to match it.
   *
   * This exists because the marketing routes are edge-cached. `next.config.ts`
   * puts `s-maxage=3600` on /tools, /ad-library, /blog and friends, and the
   * `Vary` header does not include Cookie — so the HTML Cloudflare hands out
   * carries whatever consent the *first* uncached visitor had baked into it.
   * Rendering from that value made every visitor to a cached page look
   * undecided: the banner reappeared for people who had already accepted,
   * and no pixel loaded for them until they accepted a second time (which,
   * measured on 2026-08-27, is why paid landing pages saw almost no browser-
   * side conversion signal). It failed the other way too — a page cached from
   * a consenting visitor shipped the pixel snippets to someone who had
   * rejected them.
   *
   * So the server never commits to a decision at all: consent is read from
   * `document.cookie` through `useSyncExternalStore`, and every consumer fails
   * closed until that happens. Nothing that depends on consent may render or
   * fire while this is false.
   */
  resolved: boolean
  /** Persist a choice and apply it to the loaded tags immediately. */
  save: (categories: ConsentCategories) => void
  /**
   * True while the region lookup that decides whether this visitor must be
   * asked at all is still in flight. The banner waits on it; everything else
   * already fails closed through `resolved`.
   */
  regionPending: boolean
  /** Whether the "Cookie Settings" dialog is open. */
  isSettingsOpen: boolean
  setSettingsOpen: (open: boolean) => void
}

export const ConsentContext = createContext<ConsentContextValue | null>(null)

/**
 * `path=/` is mandatory: a cookie written without it is scoped to the current
 * page's directory, so the decision would be invisible on every other route and
 * the banner would reappear as the visitor moves around the site.
 */
function writeConsentCookie(value: string): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

/**
 * The consent cookie modelled as an external store, so the provider can read it
 * during the client render instead of assigning state from an effect.
 *
 * `getServerSnapshot` deliberately returns a sentinel rather than the cookie:
 * the server render (and the hydration pass that has to match it) must not
 * commit to a decision, because the HTML it produces is edge-cached and reused
 * for other visitors. React swaps in the client snapshot right after hydration.
 */
const UNRESOLVED = Symbol.for('soku.consent.unresolved')

/** The raw cookie value, or the sentinel for "not read in a browser yet". */
type ConsentCookieSnapshot = string | undefined | typeof UNRESOLVED

const cookieListeners = new Set<() => void>()

function subscribeToConsentCookie(onChange: () => void): () => void {
  cookieListeners.add(onChange)
  return () => cookieListeners.delete(onChange)
}

/** Nothing observes `document.cookie`, so `save()` announces its own write. */
function notifyConsentCookieChanged(): void {
  for (const listener of cookieListeners) listener()
}

function readConsentCookie(): string | undefined {
  return document.cookie
    .split('; ')
    .find(entry => entry.startsWith(`${CONSENT_COOKIE_NAME}=`))
    ?.slice(CONSENT_COOKIE_NAME.length + 1)
}

function getServerConsentSnapshot(): typeof UNRESOLVED {
  return UNRESOLVED
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [isSettingsOpen, setSettingsOpen] = useState(false)

  const raw = useSyncExternalStore<ConsentCookieSnapshot>(
    subscribeToConsentCookie,
    readConsentCookie,
    getServerConsentSnapshot,
  )
  const resolved = raw !== UNRESOLVED
  const decision = useMemo(
    () => (raw === UNRESOLVED ? null : parseConsent(raw)),
    [raw],
  )

  // Publish to the plain analytics modules synchronously during render, not in
  // an effect: `track()` can be called from a handler that runs before effects
  // have flushed, and the default there is "no consent". Unresolved means the
  // cookie has not been read yet, which is also "no consent".
  setEffectiveConsent({
    analytics: resolved && decision?.analytics === true,
    marketing: resolved && decision?.marketing === true,
  })

  const save = useCallback((categories: ConsentCategories) => {
    const decidedAt = Math.floor(Date.now() / 1000)
    writeConsentCookie(serializeConsent(categories, decidedAt))
    setEffectiveConsent(categories)
    notifyConsentCookieChanged()
    setSettingsOpen(false)
  }, [])

  // Outside the EEA / UK / Switzerland there is nothing to ask, so an
  // undecided visitor is recorded as consenting and never sees a banner. The
  // country can only be resolved at request time — the marketing HTML is
  // edge-cached without `Vary: Cookie`, so a value rendered into it would
  // belong to whoever populated the cache.
  //
  // Same posture as everything else here while the answer is outstanding:
  // undecided means nothing fires.
  const [regionAsked, setRegionAsked] = useState(false)
  const regionCheckedRef = useRef(false)
  // A stored decision — including the implied one written by the lookup below —
  // is itself the answer, so only a visitor with no cookie ever waits.
  const regionPending = resolved && decision === null && !regionAsked
  useEffect(() => {
    if (!resolved || decision !== null) return
    if (regionCheckedRef.current) return
    regionCheckedRef.current = true
    let active = true
    void fetch('/api/consent-region', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .then((body: { requiresConsent?: boolean } | null) => {
        if (!active) return
        // A failed or unparseable lookup asks, like an unknown country does.
        if (body?.requiresConsent === false) save(CONSENT_ALL)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setRegionAsked(true)
      })
    return () => {
      active = false
    }
  }, [resolved, decision, save])

  const value = useMemo(
    () => ({
      decision,
      resolved,
      regionPending,
      save,
      isSettingsOpen,
      setSettingsOpen,
    }),
    [decision, resolved, regionPending, save, isSettingsOpen],
  )

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  )
}

export function useConsent(): ConsentContextValue {
  const value = useContext(ConsentContext)
  if (!value) {
    throw new Error('useConsent must be used within a ConsentProvider')
  }
  return value
}

/**
 * Consent for components that may render outside the provider (e.g. isolated
 * tests or the artifact viewer). Absent a provider nothing is consented, which
 * fails closed — and so does an unresolved decision, because the value the
 * server rendered may have come from another visitor's cached page.
 */
export function useOptionalConsent(): ConsentDecision | null {
  const context = useContext(ConsentContext)
  if (!context?.resolved) return null
  return context.decision
}
